import { useAuth } from '@/hooks/useAuth';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase/config';
import {
  Loader2, Music, Plus, Search, Pencil, Trash2, X, Users,
  Image as ImageIcon, Palette, Info as InfoIcon, AlertTriangle, ListMusic,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { RichTextarea } from '@/components/ui/rich-textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { saveBandaSettings, adminCreateBanda, adminDeleteBanda, searchUsersByNickname } from '@/lib/actions';
import { Banda } from '@/lib/types';

const TIPOS: Banda['tipo'][] = ['Agrupación Musical', 'Cornetas y Tambores', 'Banda de Música', 'Capilla'];
type FoundUser = { id: string; nickname: string; profileImageUrl: string | null };

function useUserNames(uids: string[]) {
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    const missing = uids.filter((u) => !names[u]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const fn = httpsCallable<{ uids: string[] }, { names: Record<string, string> }>(functions, 'getPublicUserNames');
        const res = await fn({ uids: missing });
        setNames((prev) => ({ ...prev, ...(res.data?.names || {}) }));
      } catch (e) { console.error('Error resolviendo nombres:', e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uids.join(',')]);
  return names;
}

/* ── Buscador + lista de miembros ── */
function MemberManager({ icon: Icon, title, accent, uids, names, onAdd, onRemove, busyUid }: {
  icon: React.ElementType; title: string; accent: string;
  uids: string[]; names: Record<string, string>;
  onAdd: (u: FoundUser) => void; onRemove: (uid: string) => void; busyUid: string | null;
}) {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<FoundUser[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      const { users } = await searchUsersByNickname(q);
      setResults(users || []);
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [term]);

  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4" style={{ color: accent }} />
        <h4 className="font-semibold">{title}</h4>
        <span className="ml-auto text-xs text-muted-foreground">{uids.length}</span>
      </div>

      <div className="relative mb-2">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar usuario para añadir..." className="pl-9" />
        {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>

      <AnimatePresence>
        {results.length > 0 && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="mb-3 space-y-1 overflow-hidden">
            {results.map((u) => {
              const already = uids.includes(u.id);
              return (
                <div key={u.id} className="flex items-center gap-2 rounded-xl border border-white/8 bg-muted/30 p-2">
                  <Avatar className="h-8 w-8"><AvatarImage src={u.profileImageUrl || undefined} /><AvatarFallback>{u.nickname?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                  <span className="flex-1 truncate text-sm">{u.nickname}</span>
                  <Button size="sm" variant={already ? 'ghost' : 'secondary'} disabled={already || busyUid === u.id} onClick={() => onAdd(u)}>
                    {busyUid === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : already ? 'Ya está' : 'Añadir'}
                  </Button>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {uids.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-3">Sin miembros.</p>
      ) : (
        <div className="space-y-1">
          <AnimatePresence initial={false}>
            {uids.map((uid) => (
              <motion.div key={uid} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 0.97 }} className="flex items-center gap-2 rounded-xl border border-white/6 bg-muted/20 p-2">
                <Avatar className="h-8 w-8"><AvatarFallback>{(names[uid] || '?').charAt(0).toUpperCase()}</AvatarFallback></Avatar>
                <span className="flex-1 truncate text-sm">{names[uid] || uid}</span>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" disabled={busyUid === uid} onClick={() => onRemove(uid)}>
                  {busyUid === uid ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />}
                </Button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

/* ── Editor de repertorio ── */
function RepertorioEditor({ items, onSave, saving }: { items: string[]; onSave: (next: string[]) => void; saving: boolean }) {
  const [list, setList] = useState<string[]>(items);
  const [nuevo, setNuevo] = useState('');
  useEffect(() => { setList(items); }, [items.join('|')]);

  const add = () => {
    const v = nuevo.trim();
    if (!v) return;
    setList((p) => [...p, v]);
    setNuevo('');
  };
  const remove = (i: number) => setList((p) => p.filter((_, idx) => idx !== i));

  return (
    <div className="rounded-2xl border border-white/8 bg-card/40 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <ListMusic className="h-4 w-4 text-primary" />
        <h4 className="font-semibold">Repertorio</h4>
        <span className="ml-auto text-xs text-muted-foreground">{list.length} marchas</span>
      </div>
      <div className="flex gap-2">
        <Input value={nuevo} onChange={(e) => setNuevo(e.target.value)} placeholder="Añadir marcha..." onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), add())} />
        <Button type="button" variant="secondary" onClick={add}><Plus className="h-4 w-4" /></Button>
      </div>
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground text-center py-2">Sin marchas en el repertorio.</p>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {list.map((m, i) => (
            <div key={`${m}-${i}`} className="flex items-center gap-2 rounded-xl border border-white/6 bg-muted/20 p-2">
              <span className="flex-1 truncate text-sm">{m}</span>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive hover:bg-destructive/10" onClick={() => remove(i)}><X className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}
      <Button onClick={() => onSave(list)} disabled={saving} className="w-full">
        {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar repertorio
      </Button>
    </div>
  );
}

/* ── Editor completo de una banda ── */
function BandaEditor({ banda, onClose, onChanged }: { banda: Banda; onClose: () => void; onChanged: () => void }) {
  const { toast } = useToast();
  const [form, setForm] = useState(() => buildForm(banda));
  const [saving, setSaving] = useState(false);
  const [savingRep, setSavingRep] = useState(false);
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setForm(buildForm(banda)); }, [banda.id]);

  const set = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));

  const names = useUserNames(banda.directores || []);

  const handleSaveInfo = async () => {
    setSaving(true);
    const updates: Partial<Banda> = {
      nombreCompleto: form.nombreCompleto.trim(),
      escudoUrl: form.escudoUrl.trim(),
      foto: form.foto.trim(),
      fundacion: Number(form.fundacion) || 0,
      componentes: Number(form.componentes) || 0,
      tipo: form.tipo as Banda['tipo'],
      colorPrimario: form.colorPrimario,
      colorSecundario: form.colorSecundario,
      info: form.info,
      bio: form.bio,
    };
    const result = await saveBandaSettings(banda.id, updates);
    if (result.success) { toast({ title: 'Guardado', description: 'Información actualizada.' }); onChanged(); }
    else toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setSaving(false);
  };

  const handleSaveRepertorio = async (next: string[]) => {
    setSavingRep(true);
    const result = await saveBandaSettings(banda.id, { repertorio: next });
    if (result.success) { toast({ title: 'Guardado', description: 'Repertorio actualizado.' }); onChanged(); }
    else toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setSavingRep(false);
  };

  const memberOp = async (uid: string, method: 'arrayUnion' | 'arrayRemove', okMsg: string) => {
    setBusyUid(uid);
    const result = await saveBandaSettings(banda.id, { directores: { _method: method, elements: [uid] } } as any);
    if (result.success) { toast({ title: 'Hecho', description: okMsg }); onChanged(); }
    else toast({ title: 'Error', description: result.message, variant: 'destructive' });
    setBusyUid(null);
  };

  const handleDelete = async () => {
    setDeleting(true);
    const result = await adminDeleteBanda(banda.id);
    if (result.success) { toast({ title: 'Eliminada', description: result.message }); onChanged(); onClose(); }
    else { toast({ title: 'Error', description: result.message, variant: 'destructive' }); setDeleting(false); }
  };

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-white/8">
          <div className="flex items-center gap-3">
            <Avatar className="h-11 w-11 border border-white/10"><AvatarImage src={form.escudoUrl} /><AvatarFallback>{form.nombreCompleto?.charAt(0)}</AvatarFallback></Avatar>
            <div className="min-w-0">
              <SheetTitle className="truncate">{form.nombreCompleto || 'Banda'}</SheetTitle>
              <SheetDescription className="truncate">{form.tipo}</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="info" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 mt-3 grid grid-cols-3">
            <TabsTrigger value="info" className="gap-1.5"><InfoIcon className="h-4 w-4" /> Info</TabsTrigger>
            <TabsTrigger value="members" className="gap-1.5"><Users className="h-4 w-4" /> Directores</TabsTrigger>
            <TabsTrigger value="rep" className="gap-1.5"><ListMusic className="h-4 w-4" /> Repertorio</TabsTrigger>
          </TabsList>

          <ScrollArea className="flex-1 min-h-0">
            {/* INFORMACIÓN */}
            <TabsContent value="info" className="px-6 py-4 space-y-5 mt-0">
              <Field label="Nombre completo"><Input value={form.nombreCompleto} onChange={(e) => set('nombreCompleto', e.target.value)} /></Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Año de fundación"><Input type="number" value={form.fundacion} onChange={(e) => set('fundacion', e.target.value)} /></Field>
                <Field label="Nº de componentes"><Input type="number" value={form.componentes} onChange={(e) => set('componentes', e.target.value)} /></Field>
              </div>

              <Field label="Tipo de banda">
                <Select value={form.tipo} onValueChange={(v) => set('tipo', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </Field>

              {/* Imágenes */}
              <div className="space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground"><ImageIcon className="h-4 w-4" /> Imágenes</p>
                <Field label="URL del escudo">
                  <div className="flex items-center gap-3">
                    <Input value={form.escudoUrl} onChange={(e) => set('escudoUrl', e.target.value)} placeholder="https://..." />
                    {form.escudoUrl && <img src={form.escudoUrl} alt="" className="h-10 w-10 rounded-lg object-cover border border-white/10 flex-shrink-0" />}
                  </div>
                </Field>
                <Field label="Foto de fondo de la tarjeta">
                  <div className="flex items-center gap-3">
                    <Input value={form.foto} onChange={(e) => set('foto', e.target.value)} placeholder="https://..." />
                    {form.foto && <img src={form.foto} alt="" className="h-10 w-16 rounded-lg object-cover border border-white/10 flex-shrink-0" />}
                  </div>
                </Field>
              </div>

              {/* Colores */}
              <div className="space-y-3">
                <p className="flex items-center gap-2 text-sm font-semibold text-muted-foreground"><Palette className="h-4 w-4" /> Colores corporativos</p>
                <div className="flex gap-6">
                  <Field label="Primario"><Input type="color" value={form.colorPrimario} onChange={(e) => set('colorPrimario', e.target.value)} className="h-11 w-20 p-1" /></Field>
                  <Field label="Secundario"><Input type="color" value={form.colorSecundario} onChange={(e) => set('colorSecundario', e.target.value)} className="h-11 w-20 p-1" /></Field>
                </div>
              </div>

              <Field label="Información"><RichTextarea rows={4} value={form.info} onChange={(e) => set('info', e.target.value)} /></Field>
              <Field label="Biografía"><RichTextarea rows={3} value={form.bio} onChange={(e) => set('bio', e.target.value)} maxLength={300} /></Field>

              <Button onClick={handleSaveInfo} disabled={saving} className="w-full">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar cambios
              </Button>

              {/* Zona peligrosa */}
              <div className="mt-6 rounded-2xl border border-destructive/30 bg-destructive/5 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-destructive mb-1"><AlertTriangle className="h-4 w-4" /> Zona peligrosa</p>
                <p className="text-xs text-muted-foreground mb-3">Eliminar la banda es permanente y no se puede deshacer.</p>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={deleting} className="gap-2">
                      {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Eliminar banda
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Eliminar "{form.nombreCompleto}"?</AlertDialogTitle>
                      <AlertDialogDescription>Esta acción es permanente. Se borrará la banda de forma definitiva.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/80">Eliminar</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </TabsContent>

            {/* DIRECTORES */}
            <TabsContent value="members" className="px-6 py-4 space-y-4 mt-0">
              <MemberManager
                icon={Users} title="Directores" accent="#60a5fa"
                uids={banda.directores || []} names={names} busyUid={busyUid}
                onAdd={(u) => memberOp(u.id, 'arrayUnion', `${u.nickname} es ahora director.`)}
                onRemove={(uid) => memberOp(uid, 'arrayRemove', 'Director eliminado.')}
              />
            </TabsContent>

            {/* REPERTORIO */}
            <TabsContent value="rep" className="px-6 py-4 space-y-4 mt-0">
              <RepertorioEditor items={banda.repertorio || []} onSave={handleSaveRepertorio} saving={savingRep} />
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function buildForm(b: Banda) {
  return {
    nombreCompleto: b.nombreCompleto || '',
    escudoUrl: b.escudoUrl || '',
    foto: b.foto || '',
    fundacion: String(b.fundacion ?? ''),
    componentes: String(b.componentes ?? ''),
    tipo: b.tipo || 'Banda de Música',
    colorPrimario: b.colorPrimario || '#9b8854',
    colorSecundario: b.colorSecundario || '#000000',
    info: b.info || '',
    bio: b.bio || '',
  };
}

/* ── Diálogo de creación ── */
function CreateBandaDialog({ onCreated }: { onCreated: () => void }) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nombreCompleto, setNombreCompleto] = useState('');
  const [tipo, setTipo] = useState<string>('Banda de Música');
  const [escudoUrl, setEscudoUrl] = useState('');

  const handleCreate = async () => {
    if (!nombreCompleto.trim()) { toast({ title: 'Falta el nombre', variant: 'destructive' }); return; }
    setCreating(true);
    const result = await adminCreateBanda({
      nombreCompleto: nombreCompleto.trim(),
      tipo: tipo as Banda['tipo'],
      escudoUrl: escudoUrl.trim() || undefined,
    });
    if (result.success) {
      toast({ title: 'Banda creada', description: result.message });
      setNombreCompleto(''); setEscudoUrl(''); setTipo('Banda de Música');
      setOpen(false);
      onCreated();
    } else {
      toast({ title: 'Error', description: result.message, variant: 'destructive' });
    }
    setCreating(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="gap-2"><Plus className="h-4 w-4" /> Crear banda</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nueva banda</DialogTitle>
          <DialogDescription>Crea la banda con lo básico; luego podrás editar todo lo demás.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre completo *"><Input value={nombreCompleto} onChange={(e) => setNombreCompleto(e.target.value)} placeholder="Ej: Banda de las Cigarreras" /></Field>
          <Field label="Tipo">
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="URL del escudo (opcional)"><Input value={escudoUrl} onChange={(e) => setEscudoUrl(e.target.value)} placeholder="https://..." /></Field>
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost" type="button">Cancelar</Button></DialogClose>
          <Button onClick={handleCreate} disabled={creating}>{creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Crear</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Página ── */
export default function AdminBandasPage() {
  const { userData, loading, jartinetData, refetch } = useAuth();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && userData?.role !== 'admin') navigate('/jartinet', { replace: true });
  }, [userData, loading, navigate]);

  const bandas = useMemo(() => {
    const list = [...(jartinetData?.bandas || [])].sort((a, b) => (a.nombreCompleto || '').localeCompare(b.nombreCompleto || ''));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((b) => `${b.nombreCompleto} ${b.tipo}`.toLowerCase().includes(q));
  }, [jartinetData, search]);

  const editing = useMemo(
    () => (jartinetData?.bandas || []).find((b) => b.id === editingId) || null,
    [jartinetData, editingId],
  );

  if (loading || userData?.role !== 'admin') {
    return (
      <div className="flex h-[calc(100vh-10rem)] w-full items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
        <p className="ml-4">Verificando permisos...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-12">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative overflow-hidden rounded-3xl border border-white/8 bg-gradient-to-br from-sky-500/15 via-card/40 to-card/20 p-8 md:p-10"
      >
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-sky-500/20 blur-3xl" />
        <div className="relative flex flex-wrap items-center justify-between gap-5">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="absolute inset-0 rounded-2xl bg-sky-500/40 blur-xl" />
              <div className="relative grid h-16 w-16 place-items-center rounded-2xl border border-sky-400/30 bg-sky-500/15 text-sky-300"><Music className="h-9 w-9" /></div>
            </div>
            <div>
              <h1 className="font-headline text-4xl font-bold text-foreground md:text-5xl">Gestión de Bandas</h1>
              <p className="mt-2 max-w-2xl text-muted-foreground">Edita toda la información, gestiona directores y repertorio, crea o elimina bandas.</p>
            </div>
          </div>
          <CreateBandaDialog onCreated={refetch} />
        </div>
      </motion.div>

      {/* Buscador */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar banda..." className="pl-9" />
      </div>

      {/* Grid */}
      {bandas.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-2xl border-2 border-dashed border-white/8 text-muted-foreground">
          <Music className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">No hay bandas{search ? ' que coincidan con la búsqueda' : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {bandas.map((b) => (
            <motion.button
              key={b.id}
              onClick={() => setEditingId(b.id)}
              whileHover={{ y: -4 }}
              className="group relative overflow-hidden rounded-2xl border border-white/8 bg-card/40 p-4 text-left transition-colors hover:border-sky-400/30"
            >
              <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${b.colorPrimario || '#9b8854'}, ${b.colorSecundario || '#000'})` }} />
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border border-white/10"><AvatarImage src={b.escudoUrl} /><AvatarFallback>{b.nombreCompleto?.charAt(0)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{b.nombreCompleto}</p>
                  <p className="text-xs text-muted-foreground truncate">{b.tipo}</p>
                  <p className="mt-1 text-[0.65rem] text-muted-foreground flex items-center gap-2">
                    <span className="flex items-center gap-0.5"><Users className="h-3 w-3" />{(b.directores || []).length}</span>
                    <span className="flex items-center gap-0.5"><ListMusic className="h-3 w-3" />{(b.repertorio || []).length}</span>
                  </p>
                </div>
                <Pencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </div>
            </motion.button>
          ))}
        </div>
      )}

      {editing && <BandaEditor banda={editing} onClose={() => setEditingId(null)} onChanged={refetch} />}
    </div>
  );
}
